import {
  type RefObject,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import useInView from "./useInView";
import useDebounceCallback from "./useDebouncCallback";

type Options = {
  pageLimit?: number;
  debounce?: number | null;
};

const defaultOptions = {
  pageLimit: 15,
  debounce: null,
};

export const usePagination = <T,>(
  data: T[],
  childRef: RefObject<HTMLDivElement | null>,
  onFinishPagination?: (() => void) | null,
  options?: Options,
) => {
  const { pageLimit, debounce } = { ...defaultOptions, ...options };
  const [currentPage, setCurrentPage] = useState(1);
  const [isFinished, setIsFinished] = useState(false);
  const isFinishedRef = useRef(false);

  // Reset pagination if data changes
  useEffect(() => {
    setCurrentPage(1);
    setIsFinished(false);
    isFinishedRef.current = false;
  }, [data]);

  const paginatedData = useMemo(() => {
    return data.slice(0, currentPage * pageLimit);
  }, [data, currentPage, pageLimit]);

  const hasMore = paginatedData.length < data.length;

  const handleInView = useCallback(() => {
    if (hasMore) {
      setCurrentPage((prev) => prev + 1);
    } else if (!isFinishedRef.current) {
      setIsFinished(true);
      isFinishedRef.current = true;
      onFinishPagination?.();
    }
  }, [hasMore, onFinishPagination]);

  useInView({
    childRef,

    onInView: debounce
      ? useDebounceCallback(handleInView, debounce)
      : handleInView,
  });

  return paginatedData;
};
